import React, { useEffect, useState } from 'react';
import { ordersService } from '@/shared/services/ordersService';
import { Order } from '@/shared/types/orders';
import styles from './OrderDetailModal.module.css';
import { couriersService } from '@/shared/services/couriersService';
import { Courier } from '@/shared/types/couriers';

interface OrderDetailModalProps {
    orderId: string;
    onClose: () => void;
}

export const OrderDetailModal: React.FC<OrderDetailModalProps> = ({ orderId, onClose }) => {
    const [order, setOrder] = useState<Order | null>(null);
    const [orderPrivate, setOrderPrivate] = useState<any>(null);
    const [courier, setCourier] = useState<Courier | null>(null);
    const [loading, setLoading] = useState(true);
    const [cancelling, setCancelling] = useState(false);
    const [error, setError] = useState('');

    // UI for Reason input
    const [showReasonInput, setShowReasonInput] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [actionType, setActionType] = useState<'CANCEL' | 'INCIDENT' | 'RESUME' | 'DELIVER' | null>(null);
    const [reasonText, setReasonText] = useState('');

    useEffect(() => {
        const fetchOrderDetails = async () => {
            setLoading(true);
            setError('');
            try {
                // Fetch public and private data in parallel
                const [publicRes, privateRes] = await Promise.allSettled([
                    ordersService.getOrder(orderId),
                    ordersService.getOrderPrivate(orderId)
                ]);

                if (publicRes.status === 'fulfilled') {
                    const fetchedOrder = publicRes.value.order;
                    setOrder(fetchedOrder);

                    // Si hay un repartidor asignado, cargar su info
                    if (fetchedOrder.assignedCourierId) {
                        try {
                            const courierRes = await couriersService.getById(fetchedOrder.assignedCourierId);
                            if (courierRes.success) {
                                setCourier(courierRes.courier);
                            }
                        } catch (cErr) {
                            console.warn('Error al cargar info del repartidor:', cErr);
                        }
                    }
                } else {
                    throw new Error('No se pudo cargar la información pública del pedido.');
                }

                if (privateRes.status === 'fulfilled') {
                    setOrderPrivate(privateRes.value.orderPrivate);
                } else {
                    console.warn('No se pudo cargar la información privada del pedido (puede ser un problema de permisos o el endpoint)');
                }
            } catch (err: any) {
                console.error('Error fetching order details:', err);
                setError('No se pudo cargar la información del pedido. Por favor intenta de nuevo.');
            } finally {
                setLoading(false);
            }
        };

        fetchOrderDetails();
    }, [orderId]);

    // Close on escape key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    if (loading) {
        return (
            <div className={styles.modalOverlay} onClick={onClose}>
                <div className={styles.modal} onClick={e => e.stopPropagation()}>
                    <div className={styles.loading}>
                        <div className={styles.loadingSpinner}></div>
                        <span>Cargando detalles premium...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !order) {
        return (
            <div className={styles.modalOverlay} onClick={onClose}>
                <div className={styles.modal} onClick={e => e.stopPropagation()}>
                    <div className={styles.header}>
                        <h2>Ups! Algo salió mal</h2>
                        <button className={styles.closeButton} onClick={onClose}>×</button>
                    </div>
                    <div className={styles.error}>
                        <span style={{ fontSize: '3rem' }}>😕</span>
                        <p>{error || 'Pedido no encontrado'}</p>
                        <button className={styles.closeBtn} onClick={onClose} style={{ marginTop: '1rem' }}>Cerrar</button>
                    </div>
                </div>
            </div>
        );
    }

    const formatDate = (timestamp?: any) => {
        if (!timestamp) return 'N/A';

        let date: Date;

        if (typeof timestamp === 'string') {
            date = new Date(timestamp);
        } else if (timestamp.seconds !== undefined) {
            date = new Date(timestamp.seconds * 1000);
        } else if (timestamp._seconds !== undefined) {
            date = new Date(timestamp._seconds * 1000);
        } else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
            date = timestamp.toDate();
        } else {
            return 'N/A';
        }

        if (isNaN(date.getTime())) return 'Fecha inválida';

        return date.toLocaleString('es-ES', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getPaymentMethodLabel = (method: string) => {
        const labels: Record<string, string> = {
            'CASH_ON_DELIVERY': 'Efectivo (Contra entrega)',
            'VPAY_SMARTLINK': 'Pago Online (VPAY)',
        };
        return labels[method] || method.replace(/_/g, ' ');
    };

    const getPaymentStatusLabel = (status: string) => {
        const labels: Record<string, string> = {
            'PENDING': 'Pendiente',
            'PAID': 'Pagado',
            'FAILED': 'Fallido',
            'EXPIRED': 'Expirado',
            'REFUNDED': 'Reembolsado',
        };
        return labels[status] || status;
    };

    const handleCancelOrder = async () => {
        if (!order) return;
        setActionType('CANCEL');
        setShowReasonInput(true);
        setReasonText('');
    };

    const handleOpenIncident = async () => {
        if (!order) return;
        setActionType('INCIDENT');
        setShowReasonInput(true);
        setReasonText('');
    };

    const handleResolveIncident = async (type: 'RESUME' | 'DELIVER' | 'CANCEL') => {
        if (!order) return;
        setActionType(type);
        setShowReasonInput(true);
        setReasonText('');
    };

    const confirmAction = async () => {
        if (!order || !actionType) return;

        if (!reasonText.trim()) {
            alert('El motivo es obligatorio.');
            return;
        }

        if (reasonText.trim().length < 10) {
            alert('El motivo debe tener al menos 10 caracteres para garantizar claridad.');
            return;
        }

        setCancelling(true);
        setError('');
        try {
            if (actionType === 'CANCEL' && order.status !== 'Incidencia') {
                await ordersService.cancelOrder(orderId, reasonText);
                setSuccessMessage('El pedido ha sido cancelado exitosamente.');
            } else if (actionType === 'INCIDENT') {
                await ordersService.openOrderIncident(orderId, reasonText);
                setSuccessMessage('El pedido ha sido puesto en incidencia exitosamente.');
            } else {
                // Tipos de resolución: RESUME, DELIVER, CANCEL (de incidencia)
                await ordersService.resolveOrderIncident(orderId, actionType as any, reasonText);
                const msgs = {
                    'RESUME': 'El pedido ha vuelto a su flujo normal.',
                    'DELIVER': 'El pedido se ha marcado como entregado.',
                    'CANCEL': 'El pedido ha sido cancelado definitivamente.'
                };
                setSuccessMessage(msgs[actionType as 'RESUME' | 'DELIVER' | 'CANCEL']);
            }
            setShowReasonInput(false);
            setShowSuccess(true);
        } catch (err: any) {
            console.error('Error performing action:', err);
            setError(`No se pudo procesar la acción. ${err.message || ''}`);
        } finally {
            setCancelling(false);
        }
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
                {showReasonInput && (
                    <div className={styles.reasonOverlay}>
                        <div className={styles.reasonBox}>
                            <h3>{actionType === 'CANCEL' ? '🚫 Cancelación de Pedido' : '⚠️ Abrir Incidencia'}</h3>
                            <p>Por favor, indica el motivo de esta acción para el registro de auditoría:</p>
                            <textarea
                                autoFocus
                                value={reasonText}
                                onChange={(e) => setReasonText(e.target.value)}
                                placeholder="Escribe el motivo aquí..."
                                className={styles.reasonTextarea}
                            />
                            <div className={styles.reasonActions}>
                                <button
                                    className={styles.closeBtn}
                                    onClick={() => setShowReasonInput(false)}
                                    disabled={cancelling}
                                >
                                    Cancelar
                                </button>
                                <button
                                    className={actionType === 'CANCEL' ? styles.confirmCancelBtn : styles.confirmIncidentBtn}
                                    onClick={confirmAction}
                                    disabled={cancelling || !reasonText.trim()}
                                >
                                    {cancelling ? 'Procesando...' : 'Confirmar Acción'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showSuccess && (
                    <div className={styles.reasonOverlay}>
                        <div className={`${styles.reasonBox} ${styles.successBox}`}>
                            <div className={styles.successIcon}>✅</div>
                            <h3>¡Operación Exitosa!</h3>
                            <p>{successMessage}</p>
                            <div className={styles.reasonActions}>
                                <button
                                    className={styles.confirmIncidentBtn}
                                    onClick={onClose}
                                >
                                    Entendido
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                <div className={styles.header}>
                    <div className={styles.headerTitle}>
                        <h2>Pedido <span style={{ color: '#ea580c' }}># {order.orderCode}</span></h2>
                        <span className={`${styles.statusBadge} ${styles[`status${order.status.replace(/\s+/g, '')}`] || ''}`}>
                            {order.status}
                        </span>
                    </div>
                    <button className={styles.closeButton} onClick={onClose}>✕</button>
                </div>

                <div className={styles.content}>
                    <div className={styles.mainInfo}>
                        {/* Notas Generales del Pedido */}
                        {order.customerNotes && (
                            <section className={styles.section} style={{ marginBottom: '1.5rem' }}>
                                <div style={{
                                    backgroundColor: '#fef2f2',
                                    border: '1px solid #ef4444',
                                    borderRadius: '0.75rem',
                                    padding: '1rem',
                                    display: 'flex',
                                    gap: '0.75rem',
                                    alignItems: 'flex-start'
                                }}>
                                    <span style={{ fontSize: '1.5rem' }}>📝</span>
                                    <div>
                                        <h3 style={{ color: '#b91c1c', margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: 700 }}>NOTA DEL CLIENTE</h3>
                                        <p style={{ color: '#dc2626', margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>
                                            "{order.customerNotes}"
                                        </p>
                                    </div>
                                </div>
                            </section>
                        )}
                        {/* Cliente Section */}
                        <section className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}>👤</div>
                                <h3>Información del Cliente</h3>
                            </div>
                            <div className={styles.card}>
                                <div className={styles.grid}>
                                    <div className={styles.infoItem}>
                                        <span className={styles.label}>Nombre completo</span>
                                        <span className={styles.value}>{orderPrivate?.fullDetails?.customerName || order.customerName || 'Información no disponible'}</span>
                                    </div>
                                    <div className={styles.infoItem}>
                                        <span className={styles.label}>WhatsApp / Teléfono</span>
                                        <span className={styles.value}>
                                            {(orderPrivate?.customerPhone || order.customerPhone) ? (
                                                <a href={`https://wa.me/${(orderPrivate?.customerPhone || order.customerPhone).replace(/\+/g, '')}`} target="_blank" rel="noreferrer" style={{ color: '#ea580c', textDecoration: 'none' }}>
                                                    {orderPrivate?.customerPhone || order.customerPhone} 📱
                                                </a>
                                            ) : 'No registrado'}
                                        </span>
                                    </div>
                                    <div className={styles.infoItem}>
                                        <span className={styles.label}>Correo Electrónico</span>
                                        <span className={styles.value}>{orderPrivate?.fullDetails?.customerEmail || order.customerEmail || 'No disponible'}</span>
                                    </div>
                                    <div className={styles.infoItem}>
                                        <span className={styles.label}>Registrado en el sistema</span>
                                        <span className={styles.value}>{formatDate(order.createdAt)}</span>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Entrega Section */}
                        <section className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}>📍</div>
                                <h3>Logística de Entrega</h3>
                            </div>
                            <div className={styles.card} style={{ background: '#fff7ed', borderLeft: '4px solid #ea580c' }}>
                                <div className={styles.infoItem}>
                                    <span className={styles.label}>Dirección / Punto de Entrega</span>
                                    <span className={styles.value} style={{ fontSize: '1.1rem' }}>
                                        {order.type === 'delivery'
                                            ? (orderPrivate?.customerExactAddress || 'Cargando dirección...')
                                            : 'Retiro en punto de venta'}
                                    </span>
                                </div>
                                {orderPrivate?.fullDetails?.dropoffExact?.instructions && (
                                    <div className={styles.infoItem} style={{ marginTop: '1rem' }}>
                                        <span className={styles.label}>Instrucciones para el repartidor</span>
                                        <span className={styles.value} style={{ fontWeight: 'normal', fontStyle: 'italic' }}>
                                            " {orderPrivate.fullDetails.dropoffExact.instructions} "
                                        </span>
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* Restaurante Section */}
                        <section className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}>🏪</div>
                                <h3>Comercio / Restaurante</h3>
                            </div>
                            <div className={styles.card}>
                                <div className={styles.grid}>
                                    <div className={styles.infoItem}>
                                        <span className={styles.label}>Establecimiento</span>
                                        <span className={styles.value}>{order.pickup.restaurantName}</span>
                                    </div>
                                    <div className={styles.infoItem}>
                                        <span className={styles.label}>Punto de Recogida</span>
                                        <span className={styles.value}>{order.pickup.addressText}</span>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Productos Section */}
                        <section className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}>🥡</div>
                                <h3>Productos del Pedido</h3>
                            </div>
                            <div className={styles.productList}>
                                {order.products.map((p, idx) => (
                                    <div key={idx} className={styles.productItem}>
                                        <div className={styles.productInfo}>
                                            <div className={styles.productQty}>{p.quantity}</div>
                                            <div className={styles.productDetails}>
                                                <span className={styles.productName}>{p.name}</span>
                                                {(p as any).notes && (
                                                    <div style={{
                                                        color: '#dc2626',
                                                        fontWeight: 700,
                                                        fontSize: '0.9rem',
                                                        marginTop: '0.25rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.25rem',
                                                        backgroundColor: '#fef2f2',
                                                        padding: '0.25rem 0.5rem',
                                                        borderRadius: '0.25rem',
                                                        width: 'fit-content'
                                                    }}>
                                                        <span>⚠️</span>
                                                        <span>Nota: {(p as any).notes}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <span className={styles.productPrice}>${p.subtotal.toFixed(2)}</span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    </div>

                    <div className={styles.sidebar}>
                        {/* Financial Card */}
                        <section className={styles.section}>
                            <div className={styles.financialsCard}>
                                <div className={styles.sectionHeader}>
                                    <div className={styles.sectionIcon}>💰</div>
                                    <h3 style={{ color: '#94a3b8' }}>Resumen de Pago</h3>
                                </div>
                                <div className={styles.financialRow}>
                                    <span>Subtotal Productos</span>
                                    <span>${order.financial.subtotal.toFixed(2)}</span>
                                </div>
                                <div className={styles.financialRow}>
                                    <span>Servicio de Envío</span>
                                    <span>${order.financial.deliveryFee.toFixed(2)}</span>
                                </div>
                                {order.financial.platformFeeAmount > 0 && (
                                    <div className={styles.financialRow}>
                                        <span>Comisión de Servicio</span>
                                        <span>${order.financial.platformFeeAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className={styles.totalRow}>
                                    <span>Total a Pagar</span>
                                    <span>${order.financial.total.toFixed(2)}</span>
                                </div>
                            </div>
                        </section>

                        {/* Payment Details */}
                        <section className={styles.section}>
                            <div className={styles.paymentCard}>
                                <div className={styles.sectionHeader}>
                                    <div className={styles.sectionIcon}>💳</div>
                                    <h3>Detalles de Transacción</h3>
                                </div>
                                <div className={styles.grid} style={{ gridTemplateColumns: '1fr' }}>
                                    <div className={styles.infoItem}>
                                        <span className={styles.label}>Método de Pago</span>
                                        <span className={styles.value} style={{ color: '#ea580c' }}>
                                            {getPaymentMethodLabel(order.payment.method)}
                                        </span>
                                    </div>
                                    <div className={styles.infoItem}>
                                        <span className={styles.label}>Estado del Pago</span>
                                        <span className={styles.value}>{getPaymentStatusLabel(order.payment.status)}</span>
                                    </div>
                                    {order.payment.vpay?.smartlinkId && (
                                        <div className={styles.infoItem}>
                                            <span className={styles.label}>VPAY SmartLink ID</span>
                                            <span className={styles.value} style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>{order.payment.vpay.smartlinkId}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </section>

                        {/* Courier Details */}
                        {order.assignedCourierId && (
                            <section className={styles.section}>
                                <div className={styles.card} style={{ borderTop: '4px solid #3b82f6' }}>
                                    <div className={styles.sectionHeader}>
                                        <div className={styles.sectionIcon} style={{ background: '#eff6ff', color: '#1d4ed8' }}>🛵</div>
                                        <h3>Repartidor Asignado</h3>
                                    </div>
                                    <div className={styles.infoItem}>
                                        <span className={styles.label}>Nombre del Repartidor</span>
                                        <span className={styles.value} style={{ fontSize: '1.2rem', color: '#1d4ed8' }}>
                                            {courier?.fullName || (loading ? 'Buscando...' : 'Desconocido')}
                                        </span>
                                    </div>
                                    <div className={styles.infoItem} style={{ marginTop: '0.5rem' }}>
                                        <span className={styles.label}>ID del Courier</span>
                                        <span className={styles.value} style={{ fontSize: '0.75rem', color: '#94a3b8', fontFamily: 'monospace' }}>
                                            {order.assignedCourierId}
                                        </span>
                                    </div>
                                </div>
                            </section>
                        )}

                        {/* Incident Details */}
                        {order.incident && (
                            <section className={styles.section}>
                                <div className={styles.card} style={{ borderTop: '4px solid #ef4444', backgroundColor: '#fef2f2' }}>
                                    <div className={styles.sectionHeader}>
                                        <div className={styles.sectionIcon} style={{ background: '#fee2e2', color: '#b91c1c' }}>⚠️</div>
                                        <h3 style={{ color: '#b91c1c' }}>Detalles de Incidencia</h3>
                                    </div>
                                    <div className={styles.infoItem}>
                                        <span className={styles.label}>Motivo (Admin)</span>
                                        <span className={styles.value} style={{ color: '#dc2626', fontWeight: 700 }}>
                                            {order.incident.reasonText}
                                        </span>
                                    </div>
                                    <div className={styles.infoItem}>
                                        <span className={styles.label}>Estado</span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <span style={{
                                                width: '8px',
                                                height: '8px',
                                                borderRadius: '50%',
                                                backgroundColor: order.incident.status === 'OPEN' ? '#ef4444' : '#22c55e'
                                            }}></span>
                                            <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                                {order.incident.status === 'OPEN' ? 'INCIDENCIA ABIERTA' : 'RESUELTA'}
                                            </span>
                                        </div>
                                    </div>
                                    {order.incident.resolutionNotes && (
                                        <div className={styles.infoItem} style={{ marginTop: '0.5rem' }}>
                                            <span className={styles.label}>Nota de Resolución</span>
                                            <span className={styles.value}>{order.incident.resolutionNotes}</span>
                                        </div>
                                    )}
                                </div>
                            </section>
                        )}
                    </div>
                </div>

                <div className={styles.footer}>
                    {order.status === 'Incidencia' ? (
                        <>
                            <button
                                className={styles.resumeBtn}
                                onClick={() => handleResolveIncident('RESUME')}
                                disabled={cancelling}
                            >
                                {cancelling ? '...' : '🔵 Reanudar Pedido'}
                            </button>
                            <button
                                className={styles.deliverBtn}
                                onClick={() => handleResolveIncident('DELIVER')}
                                disabled={cancelling}
                            >
                                {cancelling ? '...' : '🟢 Marcar Entregado'}
                            </button>
                            <button
                                className={styles.cancelBtn}
                                onClick={() => handleResolveIncident('CANCEL')}
                                disabled={cancelling}
                            >
                                {cancelling ? '...' : '🔴 Cancelar Pedido'}
                            </button>
                        </>
                    ) : (
                        order.status !== 'Cancelado' && order.status !== 'Rechazado' && order.status !== 'Entregado' && (
                            <>
                                <button
                                    className={styles.incidentBtn}
                                    onClick={handleOpenIncident}
                                    disabled={cancelling}
                                >
                                    {cancelling ? 'Procesando...' : '⚠️ Abrir Incidencia'}
                                </button>
                                <button
                                    className={styles.cancelBtn}
                                    onClick={handleCancelOrder}
                                    disabled={cancelling}
                                >
                                    {cancelling ? 'Procesando...' : '🚫 Cancelar Pedido'}
                                </button>
                            </>
                        )
                    )}
                    <button className={styles.closeBtn} onClick={onClose} disabled={cancelling}>Cerrar</button>
                </div>
            </div>
        </div>
    );
};

